import { toInteger, toNumber, round } from 'lodash'
import {
  always,
  append,
  assoc,
  complement,
  concat,
  dissocPath,
  either,
  evolve,
  ifElse,
  isEmpty,
  keys,
  lensPath,
  map,
  mergeLeft,
  omit,
  path,
  pick,
  pickAll,
  pickBy,
  pipe,
  prop,
  propEq,
  reject,
  set,
  startsWith,
  trim,
  values,
  when,
  identity,
  pathOr,
  filter,
  findIndex,
  find
} from 'ramda'
import { batch } from 'react-redux'
import { toast } from '@/components/toast-notifications'
import selectorBuilder from '@/models/selector-builder'
import queryClient from '@/components/query-client'
import {
  callOr,
  defaultIfEmpty,
  defaultIfFalsy,
  isFalsy,
  isTruthy,
  mergeSpec,
  renameKeys
} from '@/utils'
import toBoolean from '@/utils/to-boolean'
import remove from '@/utils/remove'
import uniquePush from '@/utils/unique-push'
import {
  activeContractsSelector,
  contractDataSelector,
  contractViewSelector,
  getContractSelector,
  groupListingContractsSelector,
  isStepCompleteSelector,
  lastActiveContractSelector,
  listingContractsSelector,
  nextContractSelector,
  termsSelector,
  userPadEnabledContractsSelector,
  contractPackageSelector
} from './selectors'
import {
  cancelContract,
  createContract,
  createCustomAddendumTemplate,
  editCustomAddendumTemplate,
  deleteCustomAddendumTemplate,
  exportContract,
  getContract,
  getContracts,
  getListingContracts,
  getGroupedTenants,
  getTenants,
  renewContract,
  sendContract,
  sendSignature,
  updateContract,
  uploadCustomContract,
  uploadSupportingDocument,
  editContractName,
  customContractLeaseTerms,
  customContractGetPackage,
  customContractRecipients,
  contractApplicants,
  customContractFieldsMetadata,
  customContractDraftFieldsMetadata,
  customContractAddRecipient,
  customContractDeleteSigner,
  customContractDeleteSignerWithDocuments,
  customContractUpdateSigner,
  customContractSendSignature,
  deleteCustomContractPackageFile,
  customContractSend,
  stopPadPayments,
  createCustomContract,
  customContractGetSignatures,
  deleteTenancyAgreement,
  contractChangeStructure,
  contractStatementOfResponsibility,
  cancelCustomContract
} from './ports'

const INITIAL_STATE = {
  terms: {},
  completedSteps: [],
  signature: null,
  loadedAll: false
}

// removing all non-digit characters except the decimal point
const toPrice = n => (n ? round(toNumber(n.replace(/[^0-9.]/g, '') || 0), 2) : 0)

const parseFeeFrequency = ifElse(
  prop('enabled'),
  ifElse(
    prop('hasFee'),
    pipe(prop('fee_frequency_txt_id'), defaultIfFalsy('free')),
    always('free')
  ),
  always('not_available')
)

const parseFees = pipe(
  data =>
    pipe(
      prop('additional_fees'),
      append(data.parking || { fee_txt_id: 'parking' }),
      append(data.storage || { fee_txt_id: 'storage' })
    )(data),
  map(
    pipe(
      evolve({
        fee_txt_id: defaultIfFalsy('misc'),
        fee: toPrice
      }),
      when(
        complement(propEq('fee_txt_id', 'misc')),
        mergeSpec({
          fee: pipe(ifElse(pipe(prop('hasFee'), isTruthy), prop('fee'), always(0))),
          name: pipe(ifElse(pipe(prop('enabled'), isTruthy), prop('name'), always(''))),
          fee_frequency_txt_id: parseFeeFrequency
        })
      ),
      pick(['fee', 'fee_txt_id', 'name', 'fee_frequency_txt_id'])
    )
  ),
  reject(fee => isFalsy(prop('fee_frequency_txt_id', fee)))
)

const parseAddendums = pipe(
  map(
    pipe(data =>
      pipe(
        pick([
          'listing_contract_addendum_template_id',
          'title',
          'data_type',
          'addendum_type',
          'content',
          'metadata'
        ]),
        evolve({
          listing_contract_addendum_template_id: callOr(toInteger, null),
          content: defaultIfFalsy(null),
          metadata: v =>
            data.data_type === 'options' ? defaultIfFalsy({})(v) : defaultIfEmpty(null)(v)
        })
      )(data)
    )
  ),
  values
)

const parseEndDate = pipe(
  ifElse(
    either(propEq('lease_type', 'month_to_month'), pipe(prop('end_date'), isFalsy)),
    always(null),
    pipe(prop('end_date'))
  )
)

const parseEndAction = pipe(
  ifElse(
    either(propEq('lease_type', 'month_to_month'), pipe(prop('lease_end_action'), isFalsy)),
    always(null),
    pipe(prop('lease_end_action'))
  )
)

const parseTenant = (data = {}) =>
  map(
    pipe(
      pick(['id']),
      renameKeys({
        id: 'user_id'
      }),
      evolve({
        user_id: toInteger
      }),
      mergeLeft(data)
    )
  )

const parseExtraUsers = pipe(
  prop('contract_extra_users'),
  map(
    pipe(
      pickAll(['first_name', 'last_name', 'dob', 'email_address', 'phone_number']),
      evolve({
        email_address: defaultIfFalsy(undefined),
        phone_number: defaultIfFalsy(undefined)
      })
    )
  )
)

const parseSigningTenants = parseTenant({ signing_required: 1 })
const parseNonSigningTenants = parseTenant({ signing_required: 0 })

const parseContractRentals = a =>
  a.contract_signing_tenants || a.contract_non_signing_tenants
    ? concat(
        parseSigningTenants(a.contract_signing_tenants || []),
        parseNonSigningTenants(a.contract_non_signing_tenants || [])
      )
    : map(
        pipe(
          renameKeys({ signature_required: 'signing_required' }),
          pick(['user_id', 'signing_required']),
          evolve({
            user_id: toNumber,
            signing_required: toNumber
          })
        ),
        a.contract_rentals
      )

const formatCustomFields = payload => {
  const customFieldTypes = payload.custom_field_types
  const customFields = omit(
    ['custom_field_types', 'custom_fields'],
    pickBy((val, key) => startsWith('custom_field')(key), payload)
  )
  const trimmedCustomFields = reject(isEmpty, map(trim, customFields))
  const customFieldKeys = keys(trimmedCustomFields)
  const formattedValues = customFieldKeys.map(val => ({
    field_txt_id: val,
    field_value: trimmedCustomFields[val],
    field_type: customFieldTypes[val]
  }))
  return formattedValues
}

const formatContractPayload = (payload, { customFields = true } = {}) =>
  pipe(
    customFields ? assoc('custom_fields', formatCustomFields(payload)) : identity,
    assoc('pet_policy', payload.pet_policy?.is_allowed ? 1 : 0),
    assoc('pet_deposit', payload.pet_policy?.pet_deposit),
    assoc('pet_deposit_due_date', payload.pet_policy?.pet_deposit_due_date),
    assoc('allow_cats', Number(payload.pet_policy?.allow_cats)),
    assoc('allow_dogs', Number(payload.pet_policy?.allow_dogs)),
    mergeSpec({
      contract_rentals: parseContractRentals,
      contract_fees: parseFees,
      end_date: parseEndDate,
      lease_end_action: parseEndAction,
      contract_extra_users: parseExtraUsers
    }),
    pick([
      'contract_addendums',
      'contract_custom_utilities',
      'contract_custom_features',
      'contract_extra_users',
      'contract_features',
      'contract_fees',
      'contract_landlords',
      'contract_rentals',
      'contract_utilities',
      'custom_fields',
      'end_date',
      'lease_end_action',
      'lease_type',
      'listing_id',
      'pet_deposit',
      'pet_deposit_due_date',
      'pet_policy',
      'allow_cats',
      'allow_dogs',
      'price',
      'price_frequency',
      'price_date_description',
      'security_deposit',
      'security_deposit_due_date',
      'start_date',
      'strata_lot_number',
      'strata_plan_number',
      'pad_requested',
      'payment_method'
    ]),
    evolve({
      contract_addendums: parseAddendums,
      contract_landlords: pipe(
        map(user => ({
          user_id: toNumber(user.user_id) || toNumber(user.id)
        }))
      ),
      end_date: defaultIfFalsy(null),
      listing_id: toInteger,
      price: toPrice,
      security_deposit: toPrice,
      security_deposit_due_date: defaultIfFalsy(null),
      start_date: defaultIfFalsy(null),
      pet_policy: toInteger,
      pet_deposit: toPrice,
      pet_deposit_due_date: defaultIfFalsy(null),
      allow_cats: defaultIfFalsy(null),
      allow_dogs: defaultIfFalsy(null),
      pad_requested: toBoolean,
      payment_method: defaultIfFalsy(null)
    })
  )(payload)

const invalidateAddendumTemplatesQuery = () =>
  queryClient.invalidateQueries({ queryKey: ['contract', 'addendum-templates'], exact: true })

const contract = {
  state: INITIAL_STATE,
  reducers: {
    clear: () => INITIAL_STATE,
    remove: (state, at) => dissocPath(at, state),
    update: (state, payload) => ({ ...state, ...payload }),
    updateAt: (state, at, payload) => set(lensPath(at), payload, state),
    patch: (state, at, payload) =>
      set(lensPath(at), pipe(path(at), mergeLeft(payload))(state), state),
    updateTerms: (state, payload) => ({
      ...state,
      terms: { ...state.terms, ...payload }
    }),
    completeStep: (state, payload) => ({
      ...state,
      completedSteps: uniquePush(payload, state.completedSteps)
    }),
    incompleteStep: (state, payload) => ({
      ...state,
      completedSteps: remove(payload, state.completedSteps)
    }),
    clearSteps: state => ({ ...state, completedSteps: [], signature: null })
  },

  effects: dispatch => ({
    load: async (contractId, rootState) => {
      try {
        const { response, body } = await getContract({ contractId })
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        dispatch.contract.patch([contractId], body)
        return body
      } catch (error) {
        return error
      }
    },
    create: async data => {
      const payload = formatContractPayload(data)
      try {
        const { response, body } = await createContract(
          { body: payload },
          {
            listingId: payload.listing_id
          }
        )
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        batch(() => {
          dispatch.contract.patch([body.id], body)
          dispatch.listing.remove([body.listing_id, 'contract'])
        })
        invalidateAddendumTemplatesQuery()
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    save: async ({ contractId, ...data }) => {
      const payload = formatContractPayload(data)
      try {
        const { response, body } = await updateContract(
          { body: payload },
          {
            contractId
          }
        )
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        batch(() => {
          dispatch.listing.patch([data.listing_id, 'contract'], body)
          dispatch.contract.patch([contractId], body)
        })
        invalidateAddendumTemplatesQuery()
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    send: async ({ contractId, listingId }) => {
      try {
        const { response, body } = await sendContract(
          {},
          {
            contractId
          }
        )
        if (response.ok) {
          toast('The contract has been successfully sent.', {
            title: 'Contract Sent!',
            type: 'success'
          })
        }
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        batch(() => {
          dispatch.listing.updateAt([listingId, 'contract', 'state_machine'], 'sent')
          dispatch.contract.updateAt([contractId, 'state_machine'], 'sent')
        })
      } catch (error) {
        return { error }
      }
    },
    async cancelContract({ contractId, listingId }) {
      try {
        const { response, body } = await cancelContract(undefined, {
          contractId
        })
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }

        batch(() => {
          dispatch.listing.updateAt([listingId, 'contract', 'state_machine'], 'draft')
          dispatch.contract.updateAt([contractId, 'state_machine'], 'draft')
        })

        return { response, body }
      } catch (error) {
        return { error }
      }
    },

    async renewContract({ contractId, ...data }) {
      const payload = formatContractPayload(data, { customFields: false })
      try {
        const { response, body } = await renewContract(
          { body: payload },
          {
            contractId
          }
        )
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        batch(() => {
          dispatch.listing.patch([data.listing_id, 'contract'], body)
          dispatch.contract.patch([contractId], body)
        })
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    async getContracts(_, state) {
      if (state.contract.loadedAll) return
      try {
        const { response, body } = await getContracts()
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        dispatch.contract.update({ ...body, loadedAll: true })
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    getListingContracts: async listingId => {
      try {
        const { response, body } = await getListingContracts({ listingId })
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        dispatch.contract.update(body)
      } catch (error) {
        return error
      }
    },
    async getTenants() {
      try {
        const { response, body } = await getTenants()
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        return { response, body }
      } catch (error) {
        return { error, body: [] }
      }
    },
    async getGroupedTenants() {
      try {
        const { response, body } = await getGroupedTenants()
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        return { response, body }
      } catch (error) {
        return { error, body: [] }
      }
    },
    async export(contractId) {
      try {
        const { response, body } = await exportContract({ contractId })
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        return { body }
      } catch (error) {
        return { error }
      }
    },
    async signContractWithoutPad({ contractId }, rootState) {
      const filteredSignature = rootState.contract.signature
      const contractInitials = rootState.contract.contract_initials
      try {
        const response = await sendSignature(
          {
            body: {
              signature_base64_data: filteredSignature.trim(),
              contract_initials: contractInitials
            }
          },
          { contractId }
        )

        if (response.response.ok) {
          dispatch.contract.patch([response.body.id], response.body)
        }
      } catch (error) {
        console.error('[contract/sendSignature]', error)
      }
    },
    async signContract({ contractId, paymentMethod }, rootState) {
      const filteredSignature = rootState.contract.signature
      const contractInitials = rootState.contract.contract_initials
      try {
        const response = await sendSignature(
          {
            body: {
              signature_base64_data: filteredSignature.trim(),
              contract_initials: contractInitials,
              payment_method: paymentMethod
            }
          },
          { contractId }
        )

        if (response.response.ok) {
          dispatch.contract.patch([response.body.id], response.body)
        }
      } catch (error) {
        console.error('[contract/sendSignature]', error)
      }
    },
    stopPadPayments: async ({ contractId }) => {
      try {
        const { response, body } = await stopPadPayments(
          {},
          {
            contractId
          }
        )
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        dispatch.contract.patch([contractId], body)
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    async createAddendumTemplate({ title, content }) {
      const payload = {
        title,
        content
      }
      try {
        const { response, body } = await createCustomAddendumTemplate({
          body: payload
        })
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        invalidateAddendumTemplatesQuery()
        return { response, body }
      } catch (error) {
        console.error('[contract/createAddendum]', error)
      }
    },
    async editAddendumTemplate({ addendumId, title, content }) {
      const payload = {
        title,
        content
      }
      try {
        const { response, body } = await editCustomAddendumTemplate(
          { body: payload },
          {
            addendumId
          }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        invalidateAddendumTemplatesQuery()
        return { response, body }
      } catch (error) {
        console.error('[contract/editAddendum]', error)
      }
    },
    async deleteAddendumTemplate({ addendumId }) {
      try {
        const { response, body } = await deleteCustomAddendumTemplate(undefined, {
          addendumId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        invalidateAddendumTemplatesQuery()
        return { response, body }
      } catch (error) {
        console.error('[contract/deleteAddendum]', error)
      }
    },
    parseListingContractCanceledSSE(message) {
      dispatch.contract.remove([message.id])
    },
    parseListingContractSendSSE(message, rootState) {
      if (rootState.contract[message.contract_id]) {
        dispatch.contract.remove([message.contract_id])
        dispatch.contract.load(message.contract_id)
      }
    },
    async uploadCustomContract({ file, contractId }) {
      try {
        const { response, body } = await uploadCustomContract(
          { contract: file },
          { contractId: contractId }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }

        dispatch.contract.patch([contractId], body)

        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    async cancelCustomContract({ contractId }) {
      try {
        const { response, body } = await cancelCustomContract(undefined, {
          contractId
        })
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        dispatch.contract.patch([contractId], body)
        dispatch.contract.remove([contractId, 'recipients'], {})
        return { response, body }
      } catch (error) {
        return { error }
      }
    },

    async uploadSupportingDocument({ files, contractId }) {
      try {
        const formattedFiles = files.reduce(
          (acc, next, currentIndex) => ({ ...acc, [`file${currentIndex}`]: next }),
          {}
        )
        const { response, body } = await uploadSupportingDocument(formattedFiles, {
          contractId: contractId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        dispatch.contract.patch([contractId], body)
        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    async updateDocumentName({ packageFileId, contractId, name }) {
      try {
        const { response, body } = await editContractName(
          { body: { file_name: name } },
          { packageFileId: packageFileId, contractId }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        dispatch.contract.updateAt([contractId], body)
        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    async updateLeaseTerms({ terms, contractId }) {
      try {
        const { response, body } = await customContractLeaseTerms(
          { body: terms },
          { contractId: contractId }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        dispatch.contract.patch([contractId, 'contract'], body)
        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    async getPackage({ contractId }) {
      try {
        const { response, body } = await customContractGetPackage({ contractId: contractId })
        if (!response.ok) {
          throw Error(body?.message || response?.statusText)
        }

        dispatch.contract.patch([contractId], body)

        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    async getRecipients({ contractId }) {
      try {
        const { response, body } = await customContractRecipients({ contractId: contractId })
        if (!response.ok) {
          throw Error(body?.message || response?.statusText)
        }

        dispatch.contract.updateAt([contractId, 'recipients'], body)

        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    async getContractApplicants({ contractId }) {
      try {
        const { response, body } = await contractApplicants({ contractId: contractId })
        if (!response.ok) {
          throw Error(body?.message || response?.statusText)
        }

        dispatch.contract.patch([contractId, 'applicants'], body?.listings)

        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    async putFieldMetadata({ packageFileId, contractId, metadata }) {
      try {
        const { response, body } = await customContractFieldsMetadata(
          { body: metadata },
          { packageFileId, contractId }
        )
        if (!response.ok) {
          throw Error(body?.message || response?.statusText)
        }
        return { response, body }
      } catch (error) {
        console.log(error)
        throw error
      }
    },
    async putDraftFieldMetadata({ packageFileId, contractId, metadata }) {
      try {
        const { response, body } = await customContractDraftFieldsMetadata(
          { body: metadata },
          { packageFileId, contractId }
        )
        if (!response.ok) {
          throw Error(body?.message || response?.statusText)
        }
        return { response, body }
      } catch (error) {
        console.log(error)
        throw error
      }
    },
    async addRecipient({ signer, contractId }) {
      try {
        const { response, body } = await customContractAddRecipient(
          { body: signer },
          { contractId }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        dispatch.contract.patch([contractId, 'recipients'], body)
        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    async removeSigner({ signerId, contractId }, rootState) {
      try {
        const { response, body } = await customContractDeleteSigner(undefined, {
          signerId,
          contractId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        const oldRecipients = pathOr([], ['contract', contractId, 'recipients'], rootState)
        const recipients = filter(complement(propEq('recipient_id', signerId)), oldRecipients)

        dispatch.contract.updateAt([contractId, 'recipients'], recipients)
        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    async removeSignerWithDocuments({ signerId, contractId }, rootState) {
      try {
        const { response, body } = await customContractDeleteSignerWithDocuments(undefined, {
          signerId,
          contractId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        const oldRecipients = pathOr([], ['contract', contractId, 'recipients'], rootState)
        const recipients = filter(complement(propEq('recipient_id', signerId)), oldRecipients)

        dispatch.contract.updateAt([contractId, 'recipients'], recipients)
        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    async updateSigner({ signerId, signer, contractId }) {
      try {
        const { response, body } = await customContractUpdateSigner(
          { body: signer },
          { signerId, contractId }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    async signing({ packageFileId, packageSignerId, signature }) {
      try {
        const { response, body } = await customContractSendSignature(
          { body: signature },
          { packageFileId: packageFileId, packageSignerId: packageSignerId }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    async removePackageFile({ fileId, contractId }) {
      try {
        dispatch.loading.setLoading()
        const { response, body } = await deleteCustomContractPackageFile(undefined, {
          contractId,
          fileId
        })
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        dispatch.contract.patch([contractId], body)
        return { response, body }
      } catch (error) {
        return { error }
      } finally {
        dispatch.loading.stopLoading()
      }
    },
    async removeTenancyAgreement({ fileId, contractId }, rootState) {
      try {
        dispatch.loading.setLoading()
        const { response, body } = await deleteTenancyAgreement(undefined, {
          contractId,
          packageFileId: fileId
        })
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }

        dispatch.contract.patch([contractId], body)
        return { response, body }
      } catch (error) {
        return { error }
      } finally {
        dispatch.loading.stopLoading()
      }
    },
    async sendCustomDocument({ contractId, packageFileId }, rootState) {
      try {
        const { response, body } = await customContractSend(
          {},
          { contractId: contractId, packageFileId: packageFileId }
        )

        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }

        dispatch.contract.patch([contractId], body)

        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    createCustomContract: async ({ listingId }) => {
      try {
        const { response, body } = await createCustomContract(
          { body: { custom_contract_terms: 'accepted' } },
          {
            listingId
          }
        )
        if (!response.ok) {
          throw Error(body.message || response.statusText)
        }
        batch(() => {
          dispatch.contract.patch([body.id], body)
          dispatch.listing.remove([body.listing_id, 'contract'])
        })
        invalidateAddendumTemplatesQuery()
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    customContractGetSignatures: async ({ contractId, tapId }) => {
      try {
        const { response, body } = await customContractGetSignatures({ contractId, tapId })

        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    async changeContractType({ contractId, contractStructure }) {
      try {
        const { response, body } = await contractChangeStructure(
          { body: { contract_structure: contractStructure } },
          { contractId: contractId }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    async saveStatementOfResponsibility({ contractId, packageFileId }) {
      try {
        const { response, body } = await contractStatementOfResponsibility(
          {},
          { contractId: contractId, packageFileId: packageFileId }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.response.statusText)
        }
        return { response, body }
      } catch (error) {
        console.log(error)
      }
    },
    parseSseCustomContractUploadInProgressSSE(message, rootState) {
      if (rootState.contract[message.listing_contract_id]) {
        const packageInfo = pathOr(
          [],
          ['contract', message.listing_contract_id, 'package_files'],
          rootState
        )

        const fileIndex = pipe(findIndex(propEq('id', message.listing_contract_package_file_id)))(
          packageInfo
        )
        const packageFile = pipe(
          find(propEq('id', message.listing_contract_package_file_id)),
          mergeLeft(message)
        )(packageInfo)

        dispatch.contract.updateAt(
          [message.listing_contract_id, 'package_files', fileIndex],
          packageFile
        )
      }
    },
    parseSseCustomContractUploadCompleteSSE(message, rootState) {
      if (rootState.contract[message.listing_contract_id]) {
        const packageInfo = pathOr(
          [],
          ['contract', message.listing_contract_id, 'package_files'],
          rootState
        )

        const fileIndex = pipe(findIndex(propEq('id', message.listing_contract_package_file_id)))(
          packageInfo
        )
        const packageFile = pipe(
          find(propEq('id', message.listing_contract_package_file_id)),
          mergeLeft(message)
        )(packageInfo)

        dispatch.contract.updateAt(
          [message.listing_contract_id, 'package_files', fileIndex],
          packageFile
        )
      }
    },
    parseSseCustomContractUploadErrorSSE(message, rootState) {
      if (rootState.contract[message.listing_contract_id]) {
        const packageInfo = pathOr(
          [],
          ['contract', message.listing_contract_id, 'package_files'],
          rootState
        )

        const fileIndex = pipe(findIndex(propEq('id', message.listing_contract_package_file_id)))(
          packageInfo
        )
        const packageFile = pipe(
          find(propEq('id', message.listing_contract_package_file_id)),
          mergeLeft(message)
        )(packageInfo)

        dispatch.contract.updateAt(
          [message.listing_contract_id, 'package_files', fileIndex],
          packageFile
        )
      }
    }
  }),
  selectors: selectorBuilder((slice, createSelector) => ({
    contractDetail(models) {
      return createSelector(
        slice(getContractSelector),
        models.listing.contractData,
        models.application.contractData,
        contractDataSelector
      )
    },
    contractView() {
      return slice(contractViewSelector)
    },
    activeContracts() {
      return slice(activeContractsSelector)
    },
    terms() {
      return slice(termsSelector)
    },
    isStepComplete() {
      return slice(isStepCompleteSelector)
    },
    listingContracts() {
      return slice(listingContractsSelector)
    },
    groupListingContracts() {
      return slice(groupListingContractsSelector)
    },
    nextContract() {
      return slice(nextContractSelector)
    },
    lastActiveContract() {
      return slice(lastActiveContractSelector)
    },
    userPadEnabledContracts() {
      return slice(userPadEnabledContractsSelector)
    },
    contractPackageInfo() {
      return slice(contractPackageSelector)
    }
  }))
}

export default contract
